import DoDisturbIcon from "@mui/icons-material/DoDisturb"
import DoneIcon from "@mui/icons-material/Done"
import { Chip, Divider, Paper, Stack } from "@mui/material"
import {
    GRID_ACTIONS_COLUMN_TYPE,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GRID_CHECKBOX_SELECTION_FIELD,
    GridColDef,
} from "@mui/x-data-grid-pro"
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro"
import { FieldViewModel } from "@omnia/types"
import { useCallback, useMemo, useState } from "react"
import CustomDataGrid from "../../app/stocks2/shared/components/CustomDataGrid"
import { Button } from "../react/buttons/Button"
import { CropCircle } from "../react/CropCircle"
import { Msg } from "../react/Msg"

interface CroppingSelectionGridProps {
    /**
     * List of available croppings.
     */
    options: FieldViewModel[]
    /**
     * The "enabled" cropping IDs
     */
    value: number[]
    /**
     * Whenever the "enabled" cropping state changes.
     */
    onChange: (value: number[]) => void
    /**
     * Whether to allow changes.
     */
    disabled?: boolean
}

type CroppingSelectionGridRow = {
    _cssColor: string
    croppingId: number
    croppingName: string
    fieldId: number
    fieldName: string
    fieldGroup?: string
    cropId?: number
    cropName?: string
    cropVarietyId?: number
    cropVarietyName?: string
    plotId?: string
    batchId?: string
    area?: number
    enabled: boolean
}

type CroppingSelectionGridColDef = GridColDef<CroppingSelectionGridRow> & {
    field: keyof CroppingSelectionGridRow | typeof GRID_ACTIONS_COLUMN_TYPE
}

const columnDefs: CroppingSelectionGridColDef[] = [
    {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
    } as CroppingSelectionGridColDef,
    {
        field: "croppingName",
        headerName: "Cropping",
        width: 200,
        renderCell: ({ formattedValue, row }) => (
            <Stack gap={1} direction="row" alignItems="center">
                <CropCircle color={row._cssColor} /> {formattedValue}
            </Stack>
        ),
        hideable: false,
    },
    // {
    //     field: "fieldName",
    //     headerName: "Field",
    //     width: 200,
    // },
    {
        field: "cropId",
        headerName: "Crop",
        width: 150,
        valueFormatter: (_, { cropName }) => cropName,
    },
    {
        field: "cropVarietyName",
        width: 150,
        headerName: "Variety",
    },
    {
        field: "fieldGroup",
        headerName: "Field Group",
    },
    {
        field: "batchId",
        headerName: "Batch ID",
    },
    {
        field: "plotId",
        headerName: "Plot ID",
    },
    {
        field: "enabled",
        headerName: "Enabled?",
        width: 100,
        type: "singleSelect",
        valueOptions: [
            { label: "Enabled", value: "true" },
            { label: "Disabled", value: "false" },
        ],
        valueFormatter: (value) => (value ? "Enabled" : "Disabled"),
        renderCell: ({ formattedValue, row: { enabled: value } }) => (
            <Chip
                icon={value ? <DoneIcon /> : <DoDisturbIcon />}
                label={formattedValue}
                color={value ? "success" : "default"}
                size="small"
                variant={value ? "filled" : "outlined"}
            />
        ),
        hideable: false,
        // This is a boolean column, we flip the sort order so that the initial sort places "true,1" before "false,0".
        sortingOrder: ["desc", "asc", undefined],
    },
]

const getRowId = (x: CroppingSelectionGridRow) => x?.croppingId

const pinnedColumns = {
    left: [GRID_CHECKBOX_SELECTION_FIELD, "croppingName"],
    right: ["enabled"],
}

export default function CroppingSelectionGrid({ options, value, onChange, disabled }: CroppingSelectionGridProps) {
    const croppings = useMemo<Omit<CroppingSelectionGridRow[], "enabled">>(
        () =>
            options.map<CroppingSelectionGridRow>((c) => ({
                _cssColor: c.cssColor,
                croppingId: c.croppingId,
                croppingName: c.getCroppingDescription(true, false, false, false),
                fieldId: c.fieldId,
                fieldName: c.fieldName,
                fieldGroup: c.fieldGroup,
                cropId: c.crop?.CropId,
                cropName: c.crop?.Name,
                cropVarietyId: c.variety?.CropVarietyId,
                cropVarietyName: c.variety?.Name,
                plotId: c.cropping.PlotId,
                batchId: c.cropping.BatchId,
                area: c.area,
                enabled: value.includes(c.croppingId),
            })),
        [options, value]
    )

    const { showPlotId, showBatchId, showFieldGroup } = useMemo(() => {
        const showPlotId = croppings.some((x) => x.plotId)
        const showBatchId = croppings.some((x) => x.batchId)
        const showFieldGroup = croppings.some((x) => x.fieldGroup)
        return { showPlotId, showBatchId, showFieldGroup }
    }, [croppings])

    const [selected, setSelected] = useState<number[]>([])

    const handleSelectionChange = useCallback((value: number[]) => setSelected(value), [])

    const selectedRows = useMemo(() => croppings.filter((x) => selected.includes(x.croppingId)), [croppings, selected])

    const { allDisabled, allEnabled } = useMemo(() => {
        const allDisabled = Boolean(selectedRows.length && selectedRows.every((x) => x.enabled === false))
        const allEnabled = Boolean(selectedRows.length && selectedRows.every((x) => x.enabled === true))
        return { allDisabled, allEnabled }
    }, [selectedRows])

    const handleToggleChange = useCallback(
        (_event: React.MouseEvent<HTMLElement>, toggle: "enabled" | "disabled") => {
            if (disabled) return

            let newValue = new Set(value)

            if (toggle === "enabled") {
                newValue = new Set([...newValue, ...selected])
            } else if (toggle === "disabled") {
                newValue = newValue.difference(new Set(selected))
            }

            // Change.
            onChange([...newValue])

            // Clear selection.
            setSelected([])
        },
        [value, selected, onChange, disabled]
    )

    const initialState = useMemo<GridInitialStatePro>(
        () => ({
            columns: {
                columnVisibilityModel: {
                    plotId: showPlotId,
                    batchId: showBatchId,
                    fieldGroup: showFieldGroup,
                },
            },
            sorting: {
                sortModel: [
                    {
                        field: "fieldName",
                        sort: "asc",
                    },
                    {
                        field: "cropId",
                        sort: "asc",
                    },
                ],
            },
        }),
        [showPlotId]
    )

    return (
        <Stack>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    display: "flex",
                    border: 0,
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    paddingX: "15px",
                    paddingY: 1,
                })}
            >
                <span>{selectedRows.length} selected</span>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <Button
                    variant="success"
                    iconClassName="fas fa-check"
                    disabled={Boolean(disabled || !selectedRows.length || allEnabled)}
                    onClick={(e) => handleToggleChange(e, "enabled")}
                >
                    <Msg>Enable</Msg>
                </Button>
                <Button
                    iconClassName="fas fa-ban"
                    disabled={Boolean(disabled || !selectedRows.length || allDisabled)}
                    onClick={(e) => handleToggleChange(e, "disabled")}
                >
                    <Msg>Disable</Msg>
                </Button>
            </Paper>
            <div style={{ height: 600, width: "100%", userSelect: "none" }}>
                <CustomDataGrid
                    getRowId={getRowId}
                    rows={croppings}
                    columns={columnDefs}
                    pinnedColumns={pinnedColumns}
                    disableColumnPinning={true}
                    initialState={initialState}
                    checkboxSelection
                    rowSelectionModel={selected}
                    onRowSelectionModelChange={handleSelectionChange}
                    disableColumnMenu={false}
                />
            </div>
        </Stack>
    )
}
